import { cloneElement, type ReactElement } from 'react'
import { useScale } from '#contexts/scaleContext.tsx'
import type { DsIconProps } from '#ui/Icon.tsx'
import { Text, type TextProps } from '#ui/Txt.tsx'
import { cn } from '#utils/cn.ts'
import { getHatchedClassFn, type TextClasses } from '#utils/ds.ts'

const hatchedClassFn = getHatchedClassFn<TextClasses>()
export interface HProps extends Omit<TextProps, 'className'> {
  icon?: ReactElement<DsIconProps>
  className?: (fn: typeof hatchedClassFn) => string
}
export interface HBaseProps extends HProps {
  level: 1 | 2 | 3 | 4 | 5 | 6
}
type Headings = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
const hSizes = {
  h1: 'text-lg',
  h2: 'text-base',
  h3: 'text-sm',
  h4: 'text-xs',
  h5: 'text-xs',
  h6: 'text-xs',
}
const hSizesLg = {
  h1: 'text-2xl',
  h2: 'text-xl',
  h3: hSizes.h1,
  h4: hSizes.h2,
  h5: hSizes.h3,
  h6: hSizes.h4,
}
const iconSizes = { h1: '20', h2: '18', h3: '16', h4: '14', h5: '14', h6: '14' }
const gaps = {
  h1: 'gap-2',
  h2: 'gap-1.5',
  h3: 'gap-1.5',
  h4: 'gap-1',
  h5: 'gap-1',
  h6: 'gap-1',
}

export function HBase({
  as,
  children,
  className,
  icon,
  level,
  ...props
}: HBaseProps) {
  const sizes = useScale() === 'lg' ? hSizesLg : hSizes
  const heading: Headings = `h${level}`
  const classes = className ? className(hatchedClassFn) : undefined

  return (
    <Text
      {...props}
      as={as ?? heading}
      className={x => x('mb-4 font-bold', cn(sizes[heading], classes))}
    >
      {icon ? (
        <span className={`flex items-center gap-2 ${gaps[heading]}`}>
          {cloneElement(icon, { size: iconSizes[heading] })}
          {children}
        </span>
      ) : (
        children
      )}
    </Text>
  )
}

export const H1 = (props: HProps) => <HBase {...props} level={1} />
export const H2 = (props: HProps) => <HBase {...props} level={2} />
export const H3 = (props: HProps) => <HBase {...props} level={3} />
export const H4 = (props: HProps) => <HBase {...props} level={4} />
export const H5 = (props: HProps) => <HBase {...props} level={5} />
export const H6 = (props: HProps) => <HBase {...props} level={6} />
